import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { saveTranslation } from "src/common/api/apiTranslation";
import { BusinessServiceDescriptor } from "src/businessServices";
import {
  createServiceForObject,
  deleteServiceForObject,
} from "src/businessServices/apiBusinessServices";
import {
  ObjectServiceExt,
  ZObjectService,
} from "src/businessServices/businessServises.types";
import { ObjectO2 } from "../../Obj2Tab/Obj2Nodes";
import { EdBusinessService } from "../../Obj2Tab/forms/ObjectForm2/EdBusinessService";
import { saveServFunctions } from "../../Obj2Tab/forms/ServSettingsForm/apiServFunctions";
import { unsavedId } from "../model/node";
import { ObjectServicesStore } from "../model/objectServices";

/**
 * Подключение и настройка бизнес-сервисов объекта.
 *
 * Логика перенесена из v1 без изменений; отличие в том, что список сервисов
 * живёт в отдельном хранилище, а не в узле дерева.
 */
export class BusinessServicesFlow {
  servUpdating = false;

  constructor(private readonly services: ObjectServicesStore) {
    makeAutoObservable<BusinessServicesFlow, "services">(this, {
      services: false,
    });
  }

  private setUpdating(on: boolean) {
    this.servUpdating = on;
  }

  async createService(objNode: ObjectO2, values: unknown) {
    const objectId = objNode.object.id;
    if (objectId === unsavedId)
      throw Error("Нельзя связать сервис с еще не сохранённым объектом");
    const { nameLoc, ...restServInfo } = values as EdBusinessService;
    const created = await createServiceForObject({ ...restServInfo, objectId });
    await saveTranslation(created.name, {
      value: created.name,
      translations: nameLoc,
    });
    await this.services.reloadServices(objectId);
  }

  async updateService(
    objNode: ObjectO2,
    bs: BusinessServiceDescriptor,
    servInfo: ZObjectService,
    values: unknown,
  ) {
    try {
      this.setUpdating(true);
      const { name, nameLoc, functions = [] } = values as EdBusinessService;
      if (name && name !== servInfo.name) {
        throw Error("Пока нет метода для изменения названия сервиса");
      }
      const tasks: Promise<unknown>[] = [];
      if (nameLoc) {
        tasks.push(
          saveTranslation(servInfo.name, {
            value: name,
            translations: nameLoc,
          }),
        );
      }
      await saveServFunctions(
        servInfo.objectId,
        servInfo.serviceId,
        functions.map((func) => ({
          ...func,
          displayFunctionName: func.displayFunctionName ?? func.functionName,
        })),
      );
      functions.forEach(({ displayNameLoc, displayFunctionName }) => {
        if (!!Object.keys(displayNameLoc).length && !!displayFunctionName) {
          tasks.push(
            saveTranslation(displayFunctionName, {
              value: displayFunctionName,
              translations: displayNameLoc,
            }),
          );
        }
      });
      tasks.push(bs.saveSettings(servInfo, values));
      await Promise.all(tasks);
      await this.services.reloadServices(objNode.object.id);
    } catch (e) {
      onError(e);
    } finally {
      this.setUpdating(false);
    }
  }

  async deleteService(objNode: ObjectO2, servInfo: ObjectServiceExt) {
    await deleteServiceForObject(servInfo);
    await this.services.reloadServices(objNode.object.id);
  }
}
